// Eventos del plan de medición (Fase 2) para Umami.
//
// Solo se envían los eventos y propiedades de esta lista blanca, con valores de
// listas cerradas, números o nombres técnicos. Así ninguna pantalla puede
// mandar por error un dato personal (documento, nombre, correo, teléfono).
// Las páginas vistas las registra Umami solo; aquí van los eventos que la URL
// no alcanza a explicar.
//
// Medición automática de páginas nuevas (sin escribir código):
// - Toda página registra su página vista, sus UTMs y su atribución.
// - Todo enlace a /registrarse, /ingresar o /renovar cuenta como cta_click, y
//   todo enlace de WhatsApp como whatsapp_click.
// - Un botón que no es enlace se mide con data-cta="solicitar|ingresar|renovar"
//   (y data-cta-ubicacion opcional). Una oferta de un aliado, con
//   data-aliado="<nombre>". Los valores deben estar en esta lista blanca.

const NUMBER = 'number';
// Nombre técnico de un campo de formulario (nunca su valor).
const FIELD_NAME = /^[a-z][a-z0-9_]{0,40}$/;

const TIPO_CLIENTE = ['nuevo', 'renovacion'];
const CANAL = ['whatsapp', 'sms', 'email'];
export const RANGOS_MONTO = ['hasta_300k', '300k_600k', '600k_1m', 'mas_1m'];
const PASOS_SOLICITUD = { 1: 'datos_personales', 2: 'oferta', 3: 'banco', 4: 'documentos' };
const PASOS_RENOVACION = { 1: 'oferta', 2: 'banco', 3: 'documentos' };

export const EVENTS = {
  cta_click: { cta_id: ['solicitar', 'ingresar', 'renovar'], ubicacion: ['menu', 'footer', 'inicio', 'landing'] },
  whatsapp_click: { ubicacion: ['sitio', 'registro', 'ingreso', 'solicitud', 'panel'] },
  simulador_usado: { simulador: ['inicio', 'creditos', 'cuotas'], monto: NUMBER, plazo_dias: NUMBER },
  registro_completado: { canal_otp: CANAL },
  registro_rechazado: { motivo: ['ya_registrado', 'contacto_en_uso', 'error'] },
  otp_verificado: { contexto: ['registro', 'ingreso', 'firma'], canal: CANAL, resultado: ['ok', 'invalido', 'error'] },
  ingreso_completado: { canal: CANAL },
  solicitud_paso_completado: {
    paso: NUMBER,
    nombre_paso: Object.values(PASOS_SOLICITUD),
    tipo_cliente: TIPO_CLIENTE,
  },
  evaluacion_completada: {
    resultado: ['preaprobado', 'negado', 'pendiente', 'error'],
    rango_monto: RANGOS_MONTO,
    tipo_cliente: TIPO_CLIENTE,
  },
  oferta_vista: { rango_monto: RANGOS_MONTO, tipo_cliente: TIPO_CLIENTE },
  oferta_aceptada: { monto: NUMBER, plazo_dias: NUMBER, tipo_cliente: TIPO_CLIENTE },
  // revenue y currency alimentan el reporte de ingresos de Umami.
  credito_firmado: { revenue: NUMBER, currency: ['COP'], plazo_dias: NUMBER, tipo_cliente: TIPO_CLIENTE },
  error_formulario: { formulario: ['registro', 'solicitud', 'firma'], campo: FIELD_NAME, motivo: ['vacio', 'formato'] },
  ingreso_rechazado: { motivo: ['sin_cuenta', 'cuenta_bloqueada', 'datos_invalidos', 'limite_otp', 'error_red', 'error'] },
  identidad_iniciada: {},
  identidad_verificada: { resultado: ['aprobada', 'rechazada'] },
  tipo_firma_elegido: { tipo: ['electronica', 'fisica'] },
  otp_reenviado: { contexto: ['registro', 'ingreso', 'firma'] },
  aliado_click: { aliado: ['crezu'], ubicacion: ['negado', 'solicitud', 'sitio'] },
  referido_compartido: { canal: ['whatsapp', 'email', 'qr'] },
};

const sentOnce = new Set();

function cleanValue(rule, value) {
  if (rule === NUMBER) {
    // En pesos colombianos el punto separa miles ("850.000"): se quitan símbolos y separadores.
    const digits = typeof value === 'string' ? value.replace(/\D/g, '') : null;
    const number = digits === null ? value : digits ? Number(digits) : NaN;
    return Number.isFinite(number) && number >= 0 ? Math.round(number) : undefined;
  }
  if (rule instanceof RegExp) {
    return typeof value === 'string' && rule.test(value) ? value : undefined;
  }
  return rule.includes(value) ? value : undefined;
}

/** Deja solo las propiedades permitidas para el evento y con valores válidos. */
export function sanitizeEventData(name, data = {}) {
  const rules = EVENTS[name];
  if (!rules) return null;
  return Object.entries(rules).reduce((clean, [key, rule]) => {
    const value = cleanValue(rule, data?.[key]);
    if (value !== undefined) clean[key] = value;
    return clean;
  }, {});
}

/** Rango de monto en pesos para evaluacion_completada. */
export function rangoMonto(monto) {
  const value = cleanValue(NUMBER, monto);
  if (value === undefined) return undefined;
  if (value <= 300000) return 'hasta_300k';
  if (value <= 600000) return '300k_600k';
  if (value <= 1000000) return '600k_1m';
  return 'mas_1m';
}

/**
 * Monto que el cliente eligió en la oferta (el mismo que se guarda en el crédito).
 * Si no movió el selector, es el cupo aprobado.
 */
export function montoElegido(oferta) {
  return oferta?.selectedQuota || oferta?.approvedQuota;
}

/** Canal del OTP según el código que usa la app ("W", "S", "E"). */
export function canalOtp(code) {
  return { W: 'whatsapp', S: 'sms', E: 'email' }[String(code || '').toUpperCase()];
}

/** Resultado de validar un OTP a partir de la respuesta de los servicios de la app. */
export function otpResultado(response) {
  if (!response?.error) return 'ok';
  return response?.message?.code === 'INVALIDTOKEN' ? 'invalido' : 'error';
}

/** Motivo de un ingreso que no avanza, según el código del backend. */
export function motivoIngresoRechazado(response) {
  if (response?.code === 'ERR_NETWORK') return 'error_red';
  return {
    NOTEXISTUSER: 'sin_cuenta',
    ACCOUNTBLOCK: 'cuenta_bloqueada',
    INVALIDCREDENTIALS: 'datos_invalidos',
    OTP_RATE_LIMIT: 'limite_otp',
  }[response?.message?.code] || 'error';
}

/** Resultado de la evaluación de crédito (statusCredit del backend). */
export function resultadoEvaluacion(status) {
  if (status === 'APRUEBA') return 'preaprobado';
  // El backend responde RECHAZA en la solicitud y RECHAZADO en la renovación.
  if (status === 'RECHAZA' || status === 'RECHAZADO') return 'negado';
  return status === 'PENDIENTE' ? 'pendiente' : undefined;
}

/** Datos del evento solicitud_paso_completado para un paso de /request-credit. */
export function pasoSolicitud(paso, tipoCliente = 'nuevo') {
  return { paso, nombre_paso: PASOS_SOLICITUD[paso], tipo_cliente: tipoCliente };
}

/** Datos del evento solicitud_paso_completado para un paso de la renovación. */
export function pasoRenovacion(paso) {
  return { paso, nombre_paso: PASOS_RENOVACION[paso], tipo_cliente: 'renovacion' };
}

/**
 * Envía un evento del plan a Umami. Devuelve true si se envió. Nunca lanza
 * errores: si Umami no cargó o está bloqueado, el evento se pierde sin afectar
 * la app.
 */
export function trackEvent(name, data) {
  const clean = sanitizeEventData(name, data);
  if (!clean) return false;
  try {
    if (typeof window === 'undefined' || typeof window.umami?.track !== 'function') return false;
    const sending = window.umami.track(name, clean);
    if (sending && typeof sending.catch === 'function') sending.catch(() => {});
    return true;
  } catch (error) {
    return false;
  }
}

/** Como trackEvent, pero una sola vez por clave mientras la página esté abierta. */
export function trackEventOnce(key, name, data) {
  if (sentOnce.has(key)) return false;
  const sent = trackEvent(name, data);
  if (sent) sentOnce.add(key);
  return sent;
}

const toFieldName = (key) => String(key).replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase();

const isEmptyValue = (value) => value === undefined || value === null || value === false
  || value === 0 || value === '0' || String(value).trim() === '';

/**
 * Un error_formulario por cada campo con error. Solo viaja el nombre del campo
 * y si estaba vacío o con formato inválido, nunca lo que escribió la persona:
 * values se usa únicamente para saber si el campo tenía algo.
 */
export function trackFormErrors(formulario, errors, values) {
  Object.entries(errors || {}).forEach(([field, message]) => {
    if (!message) return;
    const motivo = isEmptyValue(values?.[field]) ? 'vacio' : 'formato';
    trackEvent('error_formulario', { formulario, campo: toFieldName(field), motivo });
  });
}

// Rutas que cuentan como CTA principal.
const CTA_PATHS = { '/registrarse': 'solicitar', '/ingresar': 'ingresar', '/renovar': 'renovar' };
const PANEL_PATHS = /^\/(micredito|perfil|detalle-credito|pagar|abonar|extencion|medios-de-pago|mis-bancos|nivel-cliente)/;

export function whatsappUbicacion(pathname) {
  if (pathname.startsWith('/registrarse')) return 'registro';
  if (pathname.startsWith('/ingresar') || pathname.startsWith('/autenticarse')) return 'ingreso';
  if (pathname.startsWith('/request-credit')) return 'solicitud';
  if (PANEL_PATHS.test(pathname)) return 'panel';
  return 'sitio';
}

// Un valor declarado en el HTML que no está en la lista blanca no se envía. En
// desarrollo se avisa en la consola para que se agregue al plan de medición.
function isDeclaredValue(event, property, value) {
  if (EVENTS[event][property].includes(value)) return true;
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn(`[Medición] ${property}="${value}" no está en el plan de medición (${event}). Agrégalo en src/services/analytics/track.js.`);
  }
  return false;
}

/**
 * Clasifica un clic en un elemento con medición declarada (data-cta o
 * data-aliado). Devuelve [evento, datos] o null.
 */
export function classifyDeclaredClick(dataset = {}, { pathname = '/', inMenu = false, inFooter = false } = {}) {
  if (dataset.aliado) {
    if (!isDeclaredValue('aliado_click', 'aliado', dataset.aliado)) return null;
    const ubicacion = pathname.startsWith('/negado') ? 'negado' : pathname.startsWith('/request-credit') ? 'solicitud' : 'sitio';
    return ['aliado_click', { aliado: dataset.aliado, ubicacion }];
  }
  if (dataset.cta) {
    if (!isDeclaredValue('cta_click', 'cta_id', dataset.cta)) return null;
    const ubicacion = dataset.ctaUbicacion || (inMenu ? 'menu' : inFooter ? 'footer' : pathname === '/' ? 'inicio' : 'landing');
    return ['cta_click', { cta_id: dataset.cta, ubicacion }];
  }
  return null;
}

/**
 * Clasifica un clic en un enlace: CTA principal (registrarse, ingresar,
 * renovar) o WhatsApp. Devuelve [evento, datos] o null.
 */
export function classifyLinkClick(href, { pathname = '/', inMenu = false, inFooter = false } = {}) {
  let url;
  try {
    url = new URL(href, 'https://galilea.co');
  } catch (error) {
    return null;
  }
  if (/(^|\.)wa\.me$|whatsapp\.com$/i.test(url.hostname)) {
    return ['whatsapp_click', { ubicacion: whatsappUbicacion(pathname) }];
  }
  const ctaId = CTA_PATHS[url.pathname.replace(/\/$/, '')];
  if (!ctaId) return null;
  const ubicacion = inMenu ? 'menu' : inFooter ? 'footer' : pathname === '/' ? 'inicio' : 'landing';
  return ['cta_click', { cta_id: ctaId, ubicacion }];
}

